/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark } from '@components/systems'
import { MONOLITH_NAV_HEIGHT } from '@components/systems/monolith/MonolithNav'
import { MonolithSelect } from '@components/systems/monolith/MonolithSelect'
import { mono, monoForm, monoShared } from '@components/systems/monolith/monolith.tokens'
import {
	MANUAL_WEB_BASE,
	manualWebList,
	manualWebPath,
	pickManualLanguage,
	saveManualLanguage,
	ManualDocumentType,
	ManualLanguageCode,
	ManualLanguageType,
	ManualProductType,
	ManualSectionType,
} from '@src/configs/manual.config'
import { SEO_SITE } from '@src/configs/seo.config'
import { Analytics } from '@utils'
import { downloadPdf } from './Manuals.page'
import { ManualIcon } from './manual/ManualIcons'
import {
	buildSearchIndex,
	flashTerms,
	searchManual,
	searchTermsOf,
	ManualSearchResultType,
} from './manual/manualSearch'

/** 폰에서 네비 아래에 붙는 Contents 줄 높이 */
const MOBILE_BAR_HEIGHT = 52
const MOBILE_MAX = 900

/**
 * 주소의 #앵커만 바꾼다. 경로까지 적는다 — index.html 의 `<base href="/">` 때문에 `#id` 만 주면
 * `/#id` 로 풀려 홈 주소가 된다.
 */
const replaceHash = (id: string) =>
	window.history.replaceState(
		window.history.state,
		'',
		`${window.location.pathname}${window.location.search}#${id}`,
	)

/** hreflang 은 언어 코드 그대로, 중국어만 간체를 밝힌다 */
const hrefLangOf = (code: string): string => (code === 'zh' ? 'zh-Hans' : code)

const chapterLabel = (aDoc: ManualDocumentType, num: string): string =>
	aDoc.labels.chapterAfter
		? `${num}${aDoc.labels.chapter}`
		: `${aDoc.labels.chapter} ${num}`

/** 목차·결과에 찍는 번호. Part 는 라벨이 따로 있어 비운다 */
const tocNumOf = (aSection: ManualSectionType): string =>
	aSection.kind === 'part' ? '' : aSection.num

/** 이 절의 부모(자기보다 얕은 직전 절). Part 밖의 번호 없는 장(Why · Quick Start)은 맨 위 */
const buildTree = (sections: ManualSectionType[]) => {
	const parentOf: { [id: string]: string | null } = {}
	const childrenOf: { [id: string]: ManualSectionType[] } = { root: [] }
	const stack: ManualSectionType[] = []
	sections.forEach(aSection => {
		while (stack.length > 0 && stack[stack.length - 1].level >= aSection.level)
			stack.pop()
		const parent = stack[stack.length - 1]
		parentOf[aSection.id] = parent === undefined ? null : parent.id
		childrenOf[aSection.id] = []
		childrenOf[parent === undefined ? 'root' : parent.id].push(aSection)
		stack.push(aSection)
	})
	return { parentOf, childrenOf }
}

/**
 * 웹 매뉴얼 — `/manuals/{code}/{lang}`, 언어당 한 장 (MANUAL_WEB_PLAN.md).
 *
 * 왼쪽(4) = 언어 드롭다운 → 검색 → 여러 단계 목차(Part · 장 · 절 · 하위 절), sticky.
 * 오른쪽(8) = 본문 전체. 절마다 `#s9.4.3` 앵커 — 번호가 10개 언어 공통이라 언어를 바꿔도 같은 절에 선다.
 * 폰(≤900)은 1열, 왼쪽 패널 전체가 Contents 줄에서 여는 드로어가 된다.
 *
 * 본문은 빌드 때 S3 에서 받은 `public/manual-web/{code}/{lang}/manual.json`(scripts/sync-manuals.js).
 * 프리렌더가 JSON 을 받은 뒤의 화면을 굽는다 — 크롤러는 본문 전체를 HTML 로 본다.
 */
const ManualReader = (props: {
	aManual: ManualProductType
	aLang: ManualLanguageType
}) => {
	const { aManual, aLang } = props
	const navigate = useNavigate()
	const location = useLocation()

	const [doc, setDoc] = React.useState<ManualDocumentType | null>(null)
	const [isFailed, setIsFailed] = React.useState(false)
	const [query, setQuery] = React.useState('')
	const [cursor, setCursor] = React.useState(0)
	const [activeId, setActiveId] = React.useState('')
	const [expanded, setExpanded] = React.useState<Set<string>>(new Set())
	const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
	const [copiedId, setCopiedId] = React.useState('')

	const pagePath = manualWebPath(aManual.code, aLang.code)

	const sideRef = React.useRef<HTMLElement>(null)
	const bodyRef = React.useRef<HTMLDivElement>(null)
	const searchRef = React.useRef<HTMLInputElement>(null)
	const lastSentRef = React.useRef('')

	// 데이터 — 언어가 바뀌면 다시 받는다
	React.useEffect(() => {
		let isAlive = true
		setDoc(null)
		setIsFailed(false)
		fetch(`${MANUAL_WEB_BASE}/${aManual.code}/${aLang.code}/manual.json`)
			.then(res => {
				if (!res.ok) throw new Error(String(res.status))
				return res.json()
			})
			.then((aDoc: ManualDocumentType) => {
				if (isAlive) setDoc(aDoc)
			})
			.catch(() => {
				if (isAlive) setIsFailed(true)
			})
		return () => {
			isAlive = false
		}
	}, [aManual.code, aLang.code])

	const tree = React.useMemo(() => buildTree(doc?.sections ?? []), [doc])
	const searchIndex = React.useMemo(() => buildSearchIndex(doc?.sections ?? []), [doc])
	const resultList = React.useMemo(
		() => searchManual(searchIndex, query),
		[searchIndex, query],
	)
	const isSearching = query.trim() !== ''

	// 주소의 #앵커로 바로 선다 (공유 링크 · 언어 전환)
	React.useEffect(() => {
		if (doc === null) return
		const id = decodeURIComponent(location.hash.slice(1))
		if (id === '') return
		window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView())
		// 언어가 바뀌어 doc 이 새로 온 경우만(location 은 일부러 안 본다). 같은 문서 안 이동은 goTo 가 한다
	}, [doc])

	// 지금 읽는 절 — 화면 위쪽 선을 넘은 마지막 절
	React.useEffect(() => {
		if (doc === null || bodyRef.current === null) return
		const elementList = Array.from(
			bodyRef.current.querySelectorAll<HTMLElement>('section[data-toc="true"]'),
		)
		let frame = 0
		const measure = () => {
			frame = 0
			const isMobile = window.innerWidth <= MOBILE_MAX
			const line = MONOLITH_NAV_HEIGHT + (isMobile ? MOBILE_BAR_HEIGHT : 0) + 32
			let current = elementList[0]?.id ?? ''
			for (const anElement of elementList) {
				if (anElement.getBoundingClientRect().top - line > 0) break
				current = anElement.id
			}
			setActiveId(current)
		}
		const onScroll = () => {
			if (frame === 0) frame = window.requestAnimationFrame(measure)
		}
		measure()
		window.addEventListener('scroll', onScroll, { passive: true })
		window.addEventListener('resize', onScroll)
		return () => {
			window.removeEventListener('scroll', onScroll)
			window.removeEventListener('resize', onScroll)
			if (frame !== 0) window.cancelAnimationFrame(frame)
		}
	}, [doc])

	// 읽는 절의 조상은 목차에서 펼친다. 사용자가 접은 다른 가지는 건드리지 않는다
	React.useEffect(() => {
		if (activeId === '') return
		setExpanded(prev => {
			const next = new Set(prev)
			let id: string | null = activeId
			while (id !== null) {
				next.add(id)
				id = tree.parentOf[id] ?? null
			}
			return next.size === prev.size ? prev : next
		})
	}, [activeId, tree])

	// 목차가 길어 읽는 절이 패널 밖에 있으면 패널만 스크롤한다(창은 움직이지 않는다)
	React.useEffect(() => {
		const side = sideRef.current
		if (side === null || isSearching) return
		const row = side.querySelector<HTMLElement>('.mnTocRow.on')
		if (row === null) return
		const sideBox = side.getBoundingClientRect()
		const rowBox = row.getBoundingClientRect()
		if (rowBox.top < sideBox.top + 160 || rowBox.bottom > sideBox.bottom - 40) {
			side.scrollTop += rowBox.top - sideBox.top - sideBox.height / 3
		}
	}, [activeId, expanded, isSearching])

	// ⌘K · Ctrl+K · / — 검색창으로
	React.useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null
			const isTyping =
				target !== null &&
				(target.tagName === 'INPUT' ||
					target.tagName === 'TEXTAREA' ||
					target.tagName === 'SELECT' ||
					target.isContentEditable)
			const isShortcut =
				((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') ||
				(event.key === '/' && !isTyping)
			if (!isShortcut) return
			event.preventDefault()
			if (window.innerWidth <= MOBILE_MAX) setIsDrawerOpen(true)
			window.setTimeout(() => searchRef.current?.focus(), 0)
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [])

	// 드로어가 열린 동안 뒤 페이지는 스크롤하지 않는다
	React.useEffect(() => {
		if (!isDrawerOpen) return
		const previous = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = previous
		}
	}, [isDrawerOpen])

	// 검색 측정 — 타이핑이 1초 멈추면 한 번. 같은 검색어는 다시 보내지 않는다
	React.useEffect(() => {
		const term = query.trim()
		if (term.length < 2 || term === lastSentRef.current) return
		const timer = window.setTimeout(() => {
			lastSentRef.current = term
			Analytics.manualSearch({
				searchTerm: term,
				manualCode: aManual.code,
				manualLanguage: aLang.code,
				results: resultList.length,
			})
		}, 1000)
		return () => window.clearTimeout(timer)
	}, [query, resultList.length, aManual.code, aLang.code])

	React.useEffect(() => setCursor(0), [query])

	const goTo = (aSection: ManualSectionType, method: 'toc' | 'search') => {
		const element = document.getElementById(aSection.id)
		if (element === null) return
		setIsDrawerOpen(false)
		replaceHash(aSection.id)
		element.scrollIntoView({ block: 'start' })
		Analytics.manualSection({
			manualCode: aManual.code,
			manualLanguage: aLang.code,
			section: aSection.num !== '' ? aSection.num : aSection.id,
			method,
		})
		if (method === 'search') {
			const prose = element.querySelector<HTMLElement>('.mnProse')
			if (prose !== null) flashTerms(prose, searchTermsOf(query))
		}
	}

	const onLinkClick =
		(aSection: ManualSectionType, method: 'toc' | 'search') =>
		(event: React.MouseEvent) => {
			if (event.metaKey || event.ctrlKey || event.shiftKey) return
			event.preventDefault()
			goTo(aSection, method)
		}

	// 본문 안 상호참조(`/manuals/pls8/ko#s9.4.3`) — 같은 페이지면 새로 불러오지 않고 그 절로 간다
	const onBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
		const anchor = (event.target as HTMLElement).closest('a')
		if (
			anchor === null ||
			anchor.hash === '' ||
			anchor.pathname !== window.location.pathname
		)
			return
		const id = decodeURIComponent(anchor.hash.slice(1))
		const aSection = doc?.sections.find(anItem => anItem.id === id)
		if (aSection === undefined) return
		event.preventDefault()
		goTo(aSection, 'toc')
	}

	const toggle = (id: string) =>
		setExpanded(prev => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})

	const changeLanguage = (code: string) => {
		saveManualLanguage(code as ManualLanguageCode)
		navigate(
			`${manualWebPath(aManual.code, code)}${activeId !== '' ? `#${activeId}` : ''}`,
		)
	}

	const copyLink = (aSection: ManualSectionType) => (event: React.MouseEvent) => {
		event.preventDefault()
		const url = `${SEO_SITE.origin}${manualWebPath(aManual.code, aLang.code)}#${
			aSection.id
		}`
		replaceHash(aSection.id)
		void navigator.clipboard?.writeText(url).then(() => {
			setCopiedId(aSection.id)
			window.setTimeout(() => setCopiedId(''), 1600)
		})
	}

	const onSearchKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Escape') {
			setQuery('')
			return
		}
		if (resultList.length === 0) return
		if (event.key === 'ArrowDown') {
			event.preventDefault()
			setCursor(prev => Math.min(prev + 1, resultList.length - 1))
		} else if (event.key === 'ArrowUp') {
			event.preventDefault()
			setCursor(prev => Math.max(prev - 1, 0))
		} else if (event.key === 'Enter') {
			event.preventDefault()
			goTo(resultList[cursor].section, 'search')
		}
	}

	// ── 목차 ─────────────────────────────────────────────────────────
	const renderToc = (list: ManualSectionType[], depth: number): React.ReactNode => (
		<ul className={`mnToc d${depth}`}>
			{list.map(aSection => {
				const kids = tree.childrenOf[aSection.id] ?? []
				if (aSection.kind === 'part') {
					return (
						<li key={aSection.id} className="mnTocPart">
							<a
								className="mnTocPartLabel"
								href={`${pagePath}#${aSection.id}`}
								onClick={onLinkClick(aSection, 'toc')}
							>
								<span className="mnTocPartNum">{aSection.num}</span>
								{aSection.title}
							</a>
							{kids.length > 0 && renderToc(kids, depth + 1)}
						</li>
					)
				}
				const isOpen = expanded.has(aSection.id)
				const isOn = aSection.id === activeId
				return (
					<li key={aSection.id}>
						<View className={`mnTocRow ${isOn ? 'on' : ''}`}>
							<a
								className="mnTocLink"
								href={`${pagePath}#${aSection.id}`}
								aria-current={isOn ? 'location' : undefined}
								onClick={onLinkClick(aSection, 'toc')}
							>
								{tocNumOf(aSection) !== '' && (
									<span className="mnTocNum">{tocNumOf(aSection)}</span>
								)}
								<span className="mnTocTitle">{aSection.title}</span>
							</a>
							{kids.length > 0 && (
								<button
									type="button"
									className={`mnTocToggle ${isOpen ? 'open' : ''}`}
									aria-expanded={isOpen}
									aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${aSection.title}`}
									onClick={() => toggle(aSection.id)}
								>
									<ManualIcon name="chevronRight" size={16} />
								</button>
							)}
						</View>
						{kids.length > 0 && isOpen && renderToc(kids, depth + 1)}
					</li>
				)
			})}
		</ul>
	)

	// ── 본문 ─────────────────────────────────────────────────────────
	const renderSection = (aSection: ManualSectionType) => {
		if (doc === null) return null
		const Tag = aSection.level <= 2 ? 'h2' : aSection.level === 3 ? 'h3' : 'h4'
		const kicker =
			aSection.kind === 'part'
				? aSection.num
				: aSection.kind === 'chapter' && aSection.num !== ''
				? chapterLabel(doc, aSection.num)
				: aSection.kind === 'appendix'
				? `${doc.labels.appendix} ${aSection.num}`
				: ''
		return (
			<section
				key={aSection.id}
				id={aSection.id}
				className={`mnSec k-${aSection.kind} l${aSection.level}`}
				data-toc="true"
			>
				{kicker !== '' && <p className="mnSecKicker">{kicker}</p>}
				<Tag className="mnSecTitle">
					{aSection.kind === 'section' && (
						<span className="mnSecNum">{aSection.num}</span>
					)}
					<span>{aSection.title}</span>
					<a
						className={`mnSecAnchor ${copiedId === aSection.id ? 'copied' : ''}`}
						href={`${pagePath}#${aSection.id}`}
						onClick={copyLink(aSection)}
						aria-label="Copy link to this section"
						title="Copy link"
					>
						{copiedId === aSection.id ? 'Copied' : '#'}
					</a>
				</Tag>
				{aSection.html !== '' && (
					<div className="mnProse" dangerouslySetInnerHTML={{ __html: aSection.html }} />
				)}
			</section>
		)
	}

	// ── SEO ──────────────────────────────────────────────────────────
	const welcome = doc?.sections.find(aSection => aSection.id === 's1.1')?.text ?? ''
	const description =
		welcome.length > 155 ? `${welcome.slice(0, 152).replace(/\s+\S*$/, '')}…` : welcome
	const jsonLd =
		doc === null
			? undefined
			: {
					'@context': 'https://schema.org',
					'@type': 'TechArticle',
					headline: `${aManual.name} — ${doc.labels.title}`,
					inLanguage: hrefLangOf(aLang.code),
					version: doc.version,
					dateModified: doc.built,
					url: `${SEO_SITE.origin}${pagePath}`,
					about: { '@type': 'Product', name: aManual.name, brand: 'Judahs' },
					publisher: { '@type': 'Organization', name: 'Judahs' },
			  }
	const activeTitle = doc?.sections.find(aSection => aSection.id === activeId)

	// 목록으로 돌아가는 길 — 데스크톱은 왼쪽 패널 맨 위, 폰은 Contents 줄 왼쪽. 둘 다 스크롤해도 붙어 있다
	const backLink = (
		<a
			className="mnBack"
			href="/manuals"
			onClick={event => {
				if (event.metaKey || event.ctrlKey || event.shiftKey) return
				event.preventDefault()
				navigate('/manuals')
			}}
		>
			<ManualIcon name="chevronLeft" size={16} />
			Manuals
		</a>
	)

	return (
		<View css={style}>
			<Seo description={description !== '' ? description : undefined} jsonLd={jsonLd} />
			<Helmet htmlAttributes={{ lang: hrefLangOf(aLang.code) }}>
				{aManual.languages.map(anAlt => (
					<link
						key={anAlt.code}
						rel="alternate"
						hrefLang={hrefLangOf(anAlt.code)}
						href={`${SEO_SITE.origin}${manualWebPath(aManual.code, anAlt.code)}`}
					/>
				))}
				<link
					rel="alternate"
					hrefLang="x-default"
					href={`${SEO_SITE.origin}${manualWebPath(aManual.code, 'en')}`}
				/>
			</Helmet>

			{/* 폰 전용 — 네비 아래 Contents 줄 */}
			<View className="mnMobileBar">
				{backLink}
				<span className="mnMobileDivider" aria-hidden="true" />
				<button
					type="button"
					className="mnMobileContents"
					aria-expanded={isDrawerOpen}
					aria-controls="mnManualSide"
					onClick={() => setIsDrawerOpen(!isDrawerOpen)}
				>
					<ManualIcon name={isDrawerOpen ? 'x' : 'list'} />
					Contents
				</button>
				<span className="mnMobileCurrent">
					{activeTitle !== undefined &&
						`${activeTitle.kind === 'section' ? `${activeTitle.num} ` : ''}${
							activeTitle.title
						}`}
				</span>
			</View>

			<View className="mnWrap mnReader">
				<aside
					id="mnManualSide"
					className={`mnSide ${isDrawerOpen ? 'open' : ''}`}
					ref={sideRef}
					aria-label="Manual contents"
				>
					{/* 언어·검색은 패널 위에 붙어 있다 — 목차가 길어 스크롤해도 사라지지 않는다 */}
					<View className="mnSideTop">
						<View className="mnSideBack">{backLink}</View>
						<View className="mnField">
							<span className="mnLabel" id="mnManualLangLabel">
								Language
							</span>
							<MonolithSelect
								id="mnManualLang"
								labelledBy="mnManualLangLabel"
								value={aLang.code}
								options={aManual.languages.map(anOption => ({
									value: anOption.code,
									label: anOption.native,
									hint: anOption.label,
								}))}
								onChange={changeLanguage}
							/>
						</View>

						<View className="mnField" role="search">
							<label className="mnLabel" htmlFor="mnManualSearch">
								Search
							</label>
							<input
								id="mnManualSearch"
								ref={searchRef}
								type="search"
								className="mnInput"
								placeholder="Topic, setting or section number"
								value={query}
								onChange={event => setQuery(event.target.value)}
								onKeyDown={onSearchKey}
								autoComplete="off"
								spellCheck={false}
							/>
						</View>
					</View>

					{isSearching ? (
						<View className="mnResults">
							<p className="mnResultsMeta" aria-live="polite">
								{resultList.length === 0
									? 'No results'
									: `${resultList.length}${resultList.length === 20 ? '+' : ''} results`}
							</p>
							<ol>
								{resultList.map((aResult: ManualSearchResultType, idx) => (
									<li key={aResult.section.id}>
										<a
											className={`mnResult ${idx === cursor ? 'on' : ''}`}
											href={`${pagePath}#${aResult.section.id}`}
											onClick={onLinkClick(aResult.section, 'search')}
											onMouseEnter={() => setCursor(idx)}
										>
											<span className="mnResultTitle">
												{tocNumOf(aResult.section) !== '' && (
													<span className="mnTocNum">{tocNumOf(aResult.section)}</span>
												)}
												{aResult.section.title}
											</span>
											<span className="mnResultSnippet">
												{aResult.snippet.map((aPart, partIdx) =>
													aPart.hit ? (
														<b key={partIdx}>{aPart.text}</b>
													) : (
														<React.Fragment key={partIdx}>{aPart.text}</React.Fragment>
													),
												)}
											</span>
										</a>
									</li>
								))}
							</ol>
						</View>
					) : (
						<nav className="mnTocNav" aria-label="Contents">
							{doc !== null && renderToc(tree.childrenOf.root, 0)}
						</nav>
					)}
				</aside>

				<div className="mnBody" ref={bodyRef} onClick={onBodyClick}>
					<header className="mnReaderHead">
						<h1 className="mnReaderTitle">
							{aManual.code === 'pls8' ? (
								<KoinosWordmark as="span" className="mnReaderWordmark" />
							) : (
								<span className="mnReaderWordmark">{aManual.name}</span>
							)}
							<span className="mnReaderSub">{doc?.labels.title ?? 'User Manual'}</span>
						</h1>
						<p className="mnReaderMeta">
							{doc !== null && <span>Version {doc.version}</span>}
							<span>{aLang.native}</span>
							<button
								type="button"
								className="mnAction"
								onClick={() => {
									Analytics.download('download_manual', {
										manual_code: aManual.code,
										manual_language: aLang.code,
										method: 'download',
									})
									void downloadPdf(aLang)
								}}
							>
								Download PDF
							</button>
						</p>
					</header>

					{isFailed && (
						<View className="mnUnavailable">
							<p>This manual is not available on the web yet.</p>
							<a
								className="mnAction"
								href={aLang.url}
								target="_blank"
								rel="noopener noreferrer"
							>
								Open the PDF
							</a>
						</View>
					)}

					{doc !== null && doc.sections.map(renderSection)}
				</div>
			</View>

			<Footer />
		</View>
	)
}

/** 주소 검사 — 웹판이 없는 제품은 목록으로, 없는 언어는 브라우저 언어(없으면 English)로 */
const ManualReaderPage = () => {
	const { code = '', lang = '' } = useParams()
	const aManual = manualWebList.find(anItem => anItem.code === code)
	if (aManual === undefined) return <Navigate to="/manuals" replace />
	const aLang = aManual.languages.find(anItem => anItem.code === lang)
	if (aLang === undefined) {
		return (
			<Navigate to={manualWebPath(aManual.code, pickManualLanguage(aManual))} replace />
		)
	}
	return <ManualReader aManual={aManual} aLang={aLang} />
}

export default ManualReaderPage

const style = css({
	...monoShared,
	...monoForm,
	position: 'relative',
	width: '100%',

	// 4:8 — 왼쪽 패널, 오른쪽 본문(760 상한)
	'.mnReader': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 'clamp(32px, 4vw, 64px)',
		alignItems: 'start',
	},

	// ── 왼쪽 패널 ──────────────────────────────────────────────────
	'.mnSide': {
		position: 'sticky',
		top: MONOLITH_NAV_HEIGHT,
		maxHeight: `calc(100vh - ${MONOLITH_NAV_HEIGHT}px)`,
		overflowY: 'auto',
		overscrollBehavior: 'contain',
		padding: '0 12px 64px 0',
		boxSizing: 'border-box',
		scrollbarWidth: 'thin',
	},
	'.mnSideTop': {
		position: 'sticky',
		top: 0,
		zIndex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: 28,
		maxWidth: 360,
		paddingTop: 40,
		paddingBottom: 8,
		background: mono.bg,
	},

	// 목차 — Part 는 키커 라벨, 장·절·하위 절은 들여쓰기로 단계를 말한다
	'.mnTocNav': {
		marginTop: 28,
		maxWidth: 360,
	},
	'.mnToc': {
		listStyle: 'none',
		margin: 0,
		padding: 0,

		'&.d2, &.d3': { paddingLeft: 14 },
	},
	'.mnTocPart': {
		marginTop: 26,

		'.mnTocPartLabel': {
			display: 'block',
			padding: '0 0 8px',
			marginBottom: 4,
			borderBottom: `1px solid ${mono.line}`,
			fontSize: 11,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			'&:hover': { color: mono.ink },
		},
		'.mnTocPartNum': {
			marginRight: 10,
			color: mono.ink,
		},
	},
	'.mnTocRow': {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: 4,

		'.mnTocLink': {
			flex: 1,
			minWidth: 0,
			display: 'flex',
			gap: 10,
			padding: '6px 0',
			fontSize: 14,
			lineHeight: 1.45,
			fontWeight: 400,
			color: mono.dim,
			transition: 'color 150ms ease-out',
			'&:hover': { color: mono.ink },
		},
		'.mnTocToggle': {
			flexShrink: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: 28,
			height: 30,
			padding: 0,
			background: 'transparent',
			border: 0,
			color: mono.faint,
			cursor: 'pointer',
			svg: { transition: 'transform 150ms ease-out' },
			'&.open svg': { transform: 'rotate(90deg)' },
			'&:hover': { color: mono.ink },
			'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: -2 },
		},

		'&.on .mnTocLink': {
			color: mono.ink,
			fontWeight: 600,
		},
		'@media (prefers-reduced-motion: reduce)': {
			'.mnTocLink, svg': { transition: 'none' },
		},
	},
	// 번호는 같은 폭으로 — 제목이 한 줄로 선다
	'.mnTocNum': {
		flexShrink: 0,
		minWidth: 30,
		fontVariantNumeric: 'tabular-nums',
		color: mono.faint,
		fontWeight: 400,
	},
	'.d2 .mnTocLink, .d3 .mnTocLink': { fontSize: 13 },
	'.d2 .mnTocNum, .d3 .mnTocNum': { minWidth: 38 },

	// 검색 결과 — 목차 자리를 대신한다
	'.mnResults': {
		marginTop: 22,
		maxWidth: 360,

		'.mnResultsMeta': {
			fontSize: 11,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			paddingBottom: 10,
			borderBottom: `1px solid ${mono.line}`,
		},
		ol: { listStyle: 'none', margin: 0, padding: 0 },
		'.mnResult': {
			display: 'block',
			padding: '12px 10px',
			margin: '0 -10px',
			borderBottom: `1px solid ${mono.line}`,
			'&.on': { background: mono.surface },
		},
		'.mnResultTitle': {
			display: 'flex',
			gap: 8,
			fontSize: 14,
			fontWeight: 500,
			color: mono.ink,
		},
		'.mnResultSnippet': {
			display: 'block',
			marginTop: 4,
			fontSize: 13,
			lineHeight: 1.5,
			color: mono.dim,
			b: { color: mono.ink, fontWeight: 600 },
		},
	},

	// ── 오른쪽 본문 ────────────────────────────────────────────────
	'.mnBody': {
		minWidth: 0,
		maxWidth: 760,
		paddingBottom: 'clamp(64px, 8.3vw, 120px)',
	},

	'.mnReaderHead': {
		padding: 'clamp(40px, 5vw, 72px) 0 clamp(32px, 4vw, 56px)',
		borderBottom: `1px solid ${mono.line}`,

		'.mnReaderTitle': {
			display: 'flex',
			flexDirection: 'column',
			gap: 10,
			fontWeight: 100,
			color: mono.ink,
		},
		'.mnReaderWordmark': {
			fontSize: 'clamp(40px, 5vw, 72px)',
			lineHeight: 1,
		},
		'.mnReaderSub': {
			fontSize: 'clamp(22px, 2.2vw, 30px)',
			fontWeight: 200,
			color: mono.dim,
		},
		'.mnReaderMeta': {
			display: 'flex',
			flexWrap: 'wrap',
			alignItems: 'baseline',
			gap: '8px 24px',
			marginTop: 24,
			fontSize: 13,
			color: mono.faint,
			fontWeight: 500,
			letterSpacing: '.02em',
		},
	},

	'.mnUnavailable': {
		padding: '48px 0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 16,
		fontSize: 17,
		color: mono.dim,
	},

	'.mnAction': {
		fontFamily: 'inherit',
		fontSize: 13,
		fontWeight: 500,
		letterSpacing: '.02em',
		color: mono.dim,
		background: 'transparent',
		border: 0,
		padding: 0,
		paddingBottom: 2,
		borderBottom: `1px solid ${mono.lineSoft}`,
		cursor: 'pointer',
		'&:hover': { color: mono.ink, borderBottomColor: mono.ink },
	},

	// 절 — 앵커로 왔을 때 제목이 네비 밑에 숨지 않게
	'.mnSec': {
		scrollMarginTop: MONOLITH_NAV_HEIGHT + 24,

		'@media (max-width: 900px)': {
			scrollMarginTop: MONOLITH_NAV_HEIGHT + MOBILE_BAR_HEIGHT + 16,
		},
	},
	'.mnSecKicker': {
		fontSize: 12,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.faint,
		marginBottom: 14,
	},
	'.mnSecTitle': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 14,
		color: mono.ink,

		'.mnSecNum': {
			flexShrink: 0,
			fontVariantNumeric: 'tabular-nums',
			color: mono.faint,
			fontWeight: 400,
		},
		'.mnSecAnchor': {
			fontSize: 14,
			fontWeight: 400,
			color: mono.faint,
			opacity: 0,
			transition: 'opacity 150ms ease-out',
			'&.copied': { opacity: 1, fontSize: 12, letterSpacing: '.08em' },
			'&:focus-visible': { opacity: 1 },
		},
		'&:hover .mnSecAnchor': { opacity: 1 },
	},
	'.k-part': {
		marginTop: 'clamp(80px, 9vw, 128px)',
		paddingTop: 28,
		borderTop: `1px solid ${mono.ink}`,
		'.mnSecTitle': {
			fontSize: 'clamp(40px, 4.6vw, 64px)',
			lineHeight: 1.05,
			fontWeight: 100,
		},
		'.mnProse': { marginTop: 20, color: mono.dim },
	},
	'.l2:not(.k-part)': {
		marginTop: 'clamp(56px, 6vw, 88px)',
		paddingTop: 'clamp(28px, 3vw, 40px)',
		borderTop: `1px solid ${mono.line}`,
		'.mnSecTitle': {
			fontSize: 'clamp(30px, 3vw, 42px)',
			lineHeight: 1.15,
			fontWeight: 200,
		},
		'.mnProse': { marginTop: 24 },
	},
	'.l3': {
		marginTop: 52,
		'.mnSecTitle': { fontSize: 23, lineHeight: 1.3, fontWeight: 400 },
		'.mnProse': { marginTop: 16 },
	},
	'.l4': {
		marginTop: 36,
		'.mnSecTitle': { fontSize: 18, lineHeight: 1.35, fontWeight: 500 },
		'.mnProse': { marginTop: 12 },
	},
	'.k-part + .l2': { borderTop: 0, marginTop: 56 },
	// 헤더 밑줄 바로 아래 첫 장 — 선을 두 번 긋지 않는다
	'.mnReaderHead + .mnSec': {
		borderTop: 0,
		marginTop: 'clamp(40px, 4vw, 56px)',
		paddingTop: 0,
	},

	// 본문 타이포 — 정책 문서(policyDoc)와 같은 760 칼럼 리듬
	'.mnProse': {
		fontSize: 16,
		lineHeight: 1.7,
		color: mono.ink,
		wordBreak: 'break-word',

		'p, ul, ol, blockquote, .mnTableWrap, figure': { margin: '0 0 18px' },
		'> :last-child': { marginBottom: 0 },
		strong: { fontWeight: 600 },
		em: { fontStyle: 'italic' },
		a: {
			color: mono.ink,
			borderBottom: `1px solid ${mono.lineInput}`,
			'&:hover': { borderBottomColor: mono.ink },
		},
		h4: {
			fontSize: 16,
			fontWeight: 600,
			margin: '32px 0 10px',
		},
		'ul, ol': { paddingLeft: 22 },
		li: { margin: '6px 0', '> p': { margin: 0 } },
		code: {
			fontFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
			fontSize: '0.86em',
			padding: '1px 5px',
			background: mono.surface,
			border: `1px solid ${mono.line}`,
			borderRadius: 2,
		},
		blockquote: {
			padding: '2px 0 2px 18px',
			borderLeft: `2px solid ${mono.lineSoft}`,
			color: mono.dim,
		},
		'.mnTableWrap': {
			overflowX: 'auto',
			margin: '24px 0 28px',
		},
		table: {
			width: '100%',
			borderCollapse: 'collapse',
			fontSize: 14,
			lineHeight: 1.5,
		},
		th: {
			textAlign: 'left',
			fontSize: 11,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			padding: '0 16px 10px 0',
			borderBottom: `1px solid ${mono.ink}`,
			whiteSpace: 'nowrap',
		},
		td: {
			padding: '11px 16px 11px 0',
			borderBottom: `1px solid ${mono.line}`,
			verticalAlign: 'top',
		},
		'.mnFig': {
			margin: '28px 0 32px',
			img: { display: 'block', width: '100%', height: 'auto' },
			figcaption: {
				marginTop: 10,
				fontSize: 12,
				lineHeight: 1.5,
				color: mono.faint,
			},
		},
		'.mnShot img, .mnPhoto img': {
			border: `1px solid ${mono.line}`,
			borderRadius: 3,
		},
		'.mnTbd': {
			display: 'inline-block',
			padding: '0 6px',
			fontSize: 12,
			fontWeight: 600,
			color: '#7A5C00',
			background: '#FFF3C4',
			borderRadius: 2,
		},
		'.mnSign': {
			marginTop: 40,
			'.mnSignMotto': { fontSize: 15, fontWeight: 500, letterSpacing: '.01em' },
			'.mnSignImg': {
				display: 'block',
				width: 220,
				maxWidth: '60%',
				height: 'auto',
				margin: '18px 0 6px',
				transform: 'rotate(-3deg)',
			},
			'.mnSignName': { fontSize: 13, lineHeight: 1.6, color: mono.dim },
		},
		'mark.mnFlash': {
			background: '#FFF1A8',
			color: 'inherit',
			borderRadius: 2,
		},
	},

	// 목록으로 — 키커급 텍스트 링크(12px .18em uppercase)
	'.mnBack': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		flexShrink: 0,
		fontSize: 12,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.dim,
		'&:hover': { color: mono.ink },
		'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 3 },
	},
	'.mnSideBack': {
		marginBottom: -4,
	},

	// ── 폰 ────────────────────────────────────────────────────────
	'.mnMobileBar': {
		display: 'none',
	},

	'@media (max-width: 900px)': {
		'.mnReader': {
			gridTemplateColumns: '1fr',
		},
		'.mnMobileBar': {
			position: 'sticky',
			top: MONOLITH_NAV_HEIGHT,
			zIndex: 3,
			display: 'flex',
			alignItems: 'center',
			gap: 16,
			height: MOBILE_BAR_HEIGHT,
			padding: '0 clamp(20px, 5vw, 72px)',
			background: mono.bg,
			borderBottom: `1px solid ${mono.line}`,
		},
		'.mnMobileDivider': {
			flexShrink: 0,
			width: 1,
			height: 18,
			background: mono.line,
		},
		// 패널(드로어) 안의 뒤로 링크는 Contents 줄과 겹치므로 폰에서는 뺀다
		'.mnSideBack': { display: 'none' },
		'.mnMobileContents': {
			flexShrink: 0,
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			padding: 0,
			background: 'transparent',
			border: 0,
			fontFamily: 'inherit',
			fontSize: 14,
			fontWeight: 500,
			color: mono.ink,
			cursor: 'pointer',
		},
		'.mnMobileCurrent': {
			minWidth: 0,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			fontSize: 13,
			color: mono.faint,
		},
		'.mnSide': {
			display: 'none',
			position: 'fixed',
			top: MONOLITH_NAV_HEIGHT + MOBILE_BAR_HEIGHT,
			left: 0,
			right: 0,
			bottom: 0,
			// Top 버튼(MonolithGoTop, 4) 위로. 네비(5)와는 겹치지 않는다 — 드로어는 네비 아래에서 시작한다
			zIndex: 5,
			maxHeight: 'none',
			padding: '0 clamp(20px, 5vw, 72px) 48px',
			background: mono.bg,
			'&.open': { display: 'block' },
		},
		'.mnSideTop': { paddingTop: 20 },
		'.mnSideTop, .mnTocNav, .mnResults': { maxWidth: 'none' },
		'.mnTocRow .mnTocLink': { padding: '9px 0' },
	},
})
